import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  GetUsersAPI,
  GetUserInfoAPI,
  CreateUserAPI,
  UpdateUserAPI,
  UpdateUserStatusAPI,
  DeleteUserAPI,
  updateProfileAPI,
  updatePasswordAPI,
} from "../../api/user";
import { LoginUserAPI } from "api/auth";
import Cookies from "js-cookie";
import { act } from "react";
import { logoutAPI } from "api/auth";
import { refreshSessionAPI } from "api/auth";
import { verifySessionAPI } from "api/auth";
import { ForgotPasswordAPI } from "api/auth";
import { ResetPasswordAPI } from "api/auth";

// Login user
export const loginUser = createAsyncThunk("auth/loginUser", async (data, { rejectWithValue }) => {
  try {
    const response = await LoginUserAPI(data);
    return response.data; // expect { user, token, ... }
  } catch (err) {
    console.log("loginUser error ======>", err);
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Restore session
export const restoreSession = createAsyncThunk(
  "auth/restoreSession",
  async (_, { rejectWithValue }) => {
    try {
      const res = await verifySessionAPI();
      return res.data.data;
    } catch {
      return rejectWithValue(null);
    }
  }
);

// Verify token

export const verifySession = createAsyncThunk(
  "session/verifyToken",
  async (_, { rejectWithValue }) => {
    try {
      const data = await verifySessionAPI();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Session expired");
    }
  }
);

// Logout user
export const logoutUser = createAsyncThunk("user/logout", async () => {
  await logoutAPI();
  return true;
});

// Get list of users
export const getUsers = createAsyncThunk("users/getUsers", async (data, { rejectWithValue }) => {
  try {
    const response = await GetUsersAPI(data);
    return response.data;
  } catch (err) {
    console.log("getUsers error ======>", err);
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ForgotPasswordAPI(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ResetPasswordAPI(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Get single user info
export const getUserInfo = createAsyncThunk(
  "users/getUserInfo",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await GetUserInfoAPI(userId);
      return response.data;
    } catch (err) {
      console.log("getUserInfo error ======>", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create user
export const createUser = createAsyncThunk(
  "users/createUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CreateUserAPI(data);
      return response.data;
    } catch (err) {
      console.log("createUser error ======>", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const response = await UpdateUserAPI({ userId, data });
      return response.data;
    } catch (err) {
      console.log("updateUser error ======>", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update profile
export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const res = await updateProfileAPI(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update password
export const updatePassword = createAsyncThunk(
  "user/updatePassword",
  async (data, { rejectWithValue }) => {
    try {
      const res = await updatePasswordAPI(data);

      if (!res?.success) {
        return rejectWithValue(res?.data.errors || "Password update failed");
      }

      return {
        success: true,
        message: res.message || "Password updated successfully",
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update password");
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await DeleteUserAPI(userId);
      return { apiRes: response.data, userId };
    } catch (err) {
      console.log("deleteUser error ======>", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update user status update
export const updateUserStatus = createAsyncThunk(
  "users/updateUserStatus",
  async ({ userId, action }, { rejectWithValue }) => {
    try {
      const response = await UpdateUserStatusAPI({ userId, action });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  list: [],
  user: null,
  selectedUser: null,
  token: null,
  isAuthenticated: false,
  authInitialized: null,
  session: null,
  lastVerifiedAt: null,

  verifying: false,
  verified: null,
  verificationError: null,
  authMessage: null,
  authMessageType: null, // "error" | "warning" | "info"

  forgotPasswordLoading: false,
  forgotPasswordError: null,
  forgotPasswordSuccess: false,
  forgotPasswordMessage: null,

  resetPasswordLoading: false,
  resetPasswordError: null,
  resetPasswordSuccess: false,
  resetPasswordMessage: null,

  loginLoading: false,
  loginError: null,

  listLoading: false,
  listError: null,

  userInfoloading: false,
  userInfoError: null,

  createLoading: false,
  createError: null,
  createSuccess: false,

  updateLoading: false,
  updateError: null,
  updateSuccess: false,

  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,

  statusUpdateLoading: false,
  statusUpdateError: null,
  statusUpdateSuccess: false,

  updateProfileLoading: false,
  updateProfileError: null,
  updateProfileSuccess: false,

  updatePasswordLoading: false,
  updatePasswordError: null,
  updatePasswordSuccess: false,

  logoutLoading: false,
  logoutError: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  },
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetUserState: () => initialState,

    forceLogout: (state) => {
      state.logoutLoading = false;
      state.logoutError = null;

      state.isAuthenticated = false;
      state.authInitialized = true;
      state.verified = false;
      state.verifying = false;

      state.user = null;
      state.token = null;
      state.session = null;
      state.lastVerifiedAt = null;

      state.authMessage = "Your session has expired. Please sign in again.";
      state.authMessageType = "warning";

      Cookies.remove("user_data");
      Cookies.remove("accessToken");
      Cookies.remove("refreshToken");

      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },
    resetCreateUserState: (state) => {
      state.createLoading = false;
      state.createError = null;
      state.createSuccess = false;
    },

    reserDeleteUserState: (state) => {
      state.deleteLoading = false;
      state.deleteError = null;
      state.deleteSuccess = false;
    },
    setUserPagination: (state, action) => {
      const { page, limit } = action.payload || {};
      if (page !== undefined) state.page = page;
      if (limit !== undefined) state.limit = limit;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearAuthMessage: (state) => {
      state.authMessage = null;
      state.authMessageType = null;
      state.verificationError = null;
      state.loginError = null;
    },
    resetForgotPasswordState: (state) => {
      state.forgotPasswordLoading = false;
      state.forgotPasswordError = null;
      state.forgotPasswordSuccess = false;
      state.forgotPasswordMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Login user
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.loginError = null;

        const { user } = action.payload.data || {};

        state.user = user || null;
        state.isAuthenticated = true;
        state.authInitialized = true;
        state.verified = true;
        state.lastVerifiedAt = Date.now();
        state.verifying = false;

        Cookies.set("user_data", JSON.stringify(user), {
          expires: 1,
          sameSite: "strict",
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = action.payload || "Login failed";
      });

    builder
      // restore
      .addCase(restoreSession.pending, (state) => {
        state.authInitialized = false;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.authInitialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.session = action.payload.session;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.authInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.session = null;
      });

    // forgot password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = null;
        state.forgotPasswordSuccess = false;
        state.forgotPasswordMessage = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true;
        state.forgotPasswordMessage = action.payload?.message || "Reset link sent successfully";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = false;
        state.forgotPasswordError = action.payload?.message || "Failed to send reset link";
      });

    // reset password

    builder
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
        state.resetPasswordSuccess = false;
        state.resetPasswordMessage = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
        state.resetPasswordMessage = action.payload?.message || "Password reset successfully";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = action.payload?.message || "Failed to reset password";
      });

    // verify session
    builder
      .addCase(verifySession.pending, (state) => {
        state.verifying = true;
        state.verificationError = null;
      })
      .addCase(verifySession.fulfilled, (state, action) => {
        state.verifying = false;
        state.verified = true;
        state.authInitialized = true;
        state.verificationError = null;

        const userData = Cookies.get("user_data");
        state.user = userData ? JSON.parse(userData) : null;
        state.isAuthenticated = Boolean(state.user);

        state.lastVerifiedAt = Date.now();
      })

      .addCase(verifySession.rejected, (state, action) => {
        state.verifying = false;
        state.verified = false;
        state.session = null;
        state.isAuthenticated = false;
        state.user = null;
        state.verificationError = action.payload || "Session invalid";
        state.authMessage = "Your session has expired. Please sign in again.";
        state.lastVerifiedAt = null;
      })

      // logout
      .addCase(logoutUser.pending, (state) => {
        state.logoutLoading = true;
        state.logoutError = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.logoutLoading = false;
        state.logoutError = null;

        state.isAuthenticated = false;
        state.verified = false;
        state.user = null;
        state.token = null;
        state.error = null;
        state.authMessage = null;
        Cookies.remove("user_data");
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.logoutLoading = false;
        state.logoutError = action.error?.message || "Logout failed";

        state.isAuthenticated = false;
        state.authInitialized = true;
        state.verified = false;
        state.verifying = false;

        state.user = null;
        state.token = null;
        state.session = null;
        state.lastVerifiedAt = null;

        Cookies.remove("user_data");
        Cookies.remove("accessToken");
        Cookies.remove("refreshToken");
      });
    // getUsers
    builder
      .addCase(getUsers.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listError = null;
        const { data } = action.payload || {};
        state.list = data.users || [];
        state.pagination = data.pagination;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload || "Failed to get users";
      });

    // getUserInfo
    builder
      .addCase(getUserInfo.pending, (state) => {
        state.userInfoloading = true;
        state.userInfoError = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.userInfoloading = false;
        state.userInfoError = null;
        state.selectedUser = action.payload || null;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.userInfoloading = false;
        state.userInfoError = action.payload || "Failed to get user info";
      });

    // createUser
    builder
      .addCase(createUser.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createError = null;
        state.createSuccess = true;
        if (action.payload) {
          state.list.unshift(action.payload.data.user);
        }
        state.createSuccess = false;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.createError = action.payload || "Failed to create user";
      });

    // updateUser
    builder
      .addCase(updateUser.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateLoading = false;

        const updated = action.payload?.data?.user;
        if (!updated || !updated._id) return;

        const idx = state.list.findIndex((u) => u._id === updated._id);
        if (idx !== -1) {
          state.list[idx] = {
            ...state.list[idx],
            ...updated,
          };
        }
        state.updateSuccess = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.updateError = action.payload || "Failed to update user";
      });

    // updateUserStatus
    builder
      .addCase(updateUserStatus.pending, (state) => {
        state.statusUpdateLoading = true;
        state.statusUpdateError = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;

        const updated = action.payload.data.user;
        if (!updated?._id) return;

        const idx = state.list.findIndex((u) => u._id === updated._id);
        if (idx !== -1) {
          state.list[idx] = {
            ...state.list[idx],
            status: updated.status,
          };
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.statusUpdateError = action.payload || "Failed to update user status";
      });

    // updateProfile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.updateProfileLoading = true;
        state.updateProfileError = null;
        state.updateProfileSuccess = false;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.updateProfileLoading = false;
        state.updateProfileSuccess = true;

        const updatedUser = action.payload?.data?.user;
        if (!updatedUser) return;

        state.user = {
          ...state.user,
          ...updatedUser,
        };
        Cookies.set("user_data", JSON.stringify(state.user), {
          expires: 1,
          sameSite: "strict",
        });
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.updateProfileLoading = false;
        state.updateProfileSuccess = false;
        state.updateProfileError = action.payload || "Failed to update profile";
      });

    // updatePassword
    builder
      .addCase(updatePassword.pending, (state) => {
        state.updatePasswordLoading = true;
        state.updatePasswordError = null;
        state.updatePasswordSuccess = false;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.updatePasswordLoading = false;
        state.updatePasswordSuccess = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.updatePasswordLoading = false;
        state.updatePasswordSuccess = false;
        state.updatePasswordError = action.payload || "Failed to update password";
      });

    // deleteUser
    builder
      .addCase(deleteUser.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = null;

        const { userId } = action.payload || {};
        if (!userId) return;

        state.list = state.list.filter((u) => u._id !== userId);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || "Failed to delete user";
      });
  },
});

export const {
  resetUserState,
  resetCreateUserState,
  setUserPagination,
  clearSelectedUser,
  logout,
  forceLogout,
  restoreSessionFromCookies,
  reserDeleteUserState,
  clearAuthMessage,
  resetForgotPasswordState,
} = userSlice.actions;

export default userSlice.reducer;
